import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, Save } from 'lucide-react';
import { createReminderTemplate, updateReminderTemplate } from '../../../../api/collections';
import { Button } from '../../../../components/ui/Button';
import { Drawer } from '../../../../components/ui/Drawer';
import { FieldLabel } from '../../../../components/ui/FieldLabel';
import { Input } from '../../../../components/ui/Input';
import { Textarea } from '../../../../components/ui/Textarea';
import { cn } from '../../../../utils/cn';
import useCaretInsert from '../hooks/useCaretInsert';
import useIdempotencyKey from '../hooks/useIdempotencyKey';
import useTemplatePreview from '../hooks/useTemplatePreview';
import TemplatePreviewPanel from './TemplatePreviewPanel';
import { showToast } from '../../../../utils/showToast';
import { CHANNEL_LABELS, apiErrorCode, isRequestOutcomeUnknown } from '../utils/collectionReminders';
import {
  bodyMaxLength,
  buildTemplateCreatePayload,
  buildTemplateUpdatePayload,
  draftFromTemplate,
  emptyTemplateDraft,
  isTemplateDraftDirty,
  problemsOfField,
  sameTemplateContent,
  templateBodyLength,
  templateDraftErrorText,
  templateInvalidProblems,
  variablesForChannel,
} from '../utils/reminderTemplates';

const NO_PROBLEMS = { errors: [], warnings: [] };

/** Contador `n / max`, en rojo si se pasa. */
function Counter({ length, max }) {
  if (!Number.isInteger(max)) return null;
  return (
    <span className={cn('text-xs tabular-nums', length > max ? 'font-semibold text-status-error' : 'text-fg-subtle')}>
      {length} / {max}
    </span>
  );
}

/**
 * Errores y avisos de billing de un campo. El mensaje llega en español y puede traer tokens
 * {{…}}: se muestra como texto, nunca por t().
 */
function ProblemList({ id, errors, warnings }) {
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <ul id={id} className="m-0 list-none space-y-1 p-0">
      {errors.map((problem, index) => (
        <li key={`e-${problem.code}-${index}`} className="flex items-start gap-1.5 text-xs text-status-error">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{problem.message}</span>
        </li>
      ))}
      {warnings.map((problem, index) => (
        <li key={`w-${problem.code}-${index}`} className="flex items-start gap-1.5 text-xs text-status-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{problem.message}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Editor de una plantilla de recordatorio (panel a pantalla completa): nombre, asunto (solo
 * correo) y texto con botones que insertan las variables del catálogo de billing en el
 * cursor; a la derecha, la vista previa en vivo que arma billing (en móvil, pestañas
 * Editar / Vista previa).
 *
 * Guardar:
 *   · al crear, con la clave de idempotencia generada al abrir: un reintento tras una
 *     respuesta perdida devuelve la plantilla ya creada en vez de duplicarla;
 *   · al editar, con `expectedVersion`: si otra persona la cambió en el medio, billing
 *     responde 409 y se ofrece cargar la versión actual (nunca se pisa en silencio). Si la
 *     respuesta se pierde (sin respuesta o 5xx), se relee la plantilla: si ya tiene lo
 *     enviado, se guardó (y la versión esperada pasa a ser la nueva, para que seguir
 *     editando no termine en un 409 contra uno mismo).
 *
 * Se monta con `key` por apertura: cada apertura empieza de cero.
 *
 * @param {{ mode: 'create'|'edit', channel: 'email'|'whatsapp', template?: object,
 *   initialDraft?: { name: string, subject: string, body: string },
 *   catalog: { variables: object[], limits: object } | null,
 *   onClose: () => void, onSaved: (saved: object, mode: 'create'|'edit') => void,
 *   onReloadTemplate: (templateId: string) => Promise<object|null|undefined> }} props
 *   `onReloadTemplate` resuelve con la plantilla vigente, `null` si ya no existe o
 *   `undefined` si no se pudo recargar.
 */
export default function ReminderTemplateEditor({
  mode,
  channel,
  template,
  initialDraft,
  catalog,
  onClose,
  onSaved,
  onReloadTemplate,
}) {
  const { t } = useTranslation();
  const baseId = useId();
  const isEmail = channel === 'email';
  const limits = catalog?.limits ?? null;

  const [base, setBase] = useState(() => ({
    id: template?.id ?? null,
    version: template?.version ?? null,
    draft: mode === 'edit' ? draftFromTemplate(template) : initialDraft ?? emptyTemplateDraft(),
  }));
  const [draft, setDraft] = useState(base.draft);
  const [target, setTarget] = useState('body');
  const [saveProblems, setSaveProblems] = useState(null);
  const [formError, setFormError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [pane, setPane] = useState('edit');
  const [source, setSource] = useState('sample');
  const [companyId, setCompanyId] = useState('');

  const idempotency = useIdempotencyKey(mode === 'create');
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  // Lo escrito AHORA (los campos siguen editables mientras se guarda): tras una respuesta
  // perdida se compara con lo que se mandó.
  const latestDraft = useRef(draft);
  latestDraft.current = draft;

  const setField = (field) => (value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setSaveProblems(null);
    setFormError(null);
    setConfirmDiscard(false);
    idempotency.onEdited();
  };
  const insertInSubject = useCaretInsert(subjectRef, draft.subject, setField('subject'));
  const insertInBody = useCaretInsert(bodyRef, draft.body, setField('body'));

  const preview = useTemplatePreview({
    channel,
    subject: draft.subject,
    body: draft.body,
    source,
    companyId,
  });

  const variables = variablesForChannel(catalog, channel);
  const dirty = isTemplateDraftDirty(channel, draft, base.draft);
  // Lo último que dijo billing de ESTE borrador: el 400 al guardar o, si no, la vista previa
  // (que valida igual, salvo el nombre, que no forma parte del mensaje). Si la vista previa
  // no se está pidiendo (texto vacío, falta el comercio) o el último pedido falló (límite de
  // pedidos, red), lo que trae es de otro borrador: marcaría errores ya corregidos y callaría
  // los nuevos.
  const previewProblems =
    preview.data && !preview.idle && !preview.error
      ? { errors: preview.data.errors ?? [], warnings: preview.data.warnings ?? [] }
      : NO_PROBLEMS;
  const problems = saveProblems ?? previewProblems;
  const fieldProblems = (field) => ({
    errors: problemsOfField(problems.errors, field),
    warnings: problemsOfField(problems.warnings, field),
  });
  const nameProblems = fieldProblems('name');
  const subjectProblems = fieldProblems('subject');
  const bodyProblems = fieldProblems('body');

  const requestClose = () => {
    if (saving) return;
    // Esc o un clic afuera no descartan lo escrito: hay que confirmarlo.
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  // Una edición sin respuesta (o con 5xx) pudo haberse guardado: se relee la plantilla.
  //   · Tiene lo que se mandó → se guardó. Si no se escribió nada después, se cierra como
  //     cualquier guardado; si no, queda abierto con la versión nueva como esperada.
  //   · Otra versión con otro contenido → la cambió otra persona: el aviso de conflicto.
  //   · Misma versión → todavía no aparece guardada: se puede volver a guardar (billing no
  //     crea otra versión si el contenido ya es el vigente).
  const resolveUncertainUpdate = async (sent) => {
    const fresh = await onReloadTemplate(base.id);
    if (fresh === undefined) {
      setFormError(t('No hubo respuesta a tiempo y no se pudo comprobar si se guardó. Volvé a guardar.'));
      return;
    }
    if (fresh === null) {
      setFormError(t('La plantilla ya no existe o fue archivada.'));
      return;
    }
    if (sameTemplateContent(channel, draftFromTemplate(fresh), sent)) {
      if (sameTemplateContent(channel, latestDraft.current, sent)) {
        onSaved(fresh, 'edit');
        return;
      }
      setBase({ id: fresh.id, version: fresh.version, draft: sent });
      showToast(
        t('La plantilla se guardó (versión {{n}}). Lo que cambiaste después sigue sin guardar.', { n: fresh.version }),
        'warning',
      );
      return;
    }
    if (fresh.version !== base.version) {
      setConflict(true);
      return;
    }
    setFormError(t('No hubo respuesta a tiempo y la plantilla todavía no aparece guardada. Volvé a guardar.'));
  };

  const handleSave = async () => {
    if (mode === 'create' && !idempotency.key) {
      setFormError(t('Este navegador no permite generar una clave segura. Actualizalo o probá con otro.'));
      return;
    }
    const sent = draft;
    setSaving(true);
    setFormError(null);
    setSaveProblems(null);
    try {
      const { data } =
        mode === 'create'
          ? await createReminderTemplate(buildTemplateCreatePayload(channel, sent, idempotency.key))
          : await updateReminderTemplate(base.id, buildTemplateUpdatePayload(channel, sent, base.version));
      onSaved(data, mode);
    } catch (err) {
      if (mode === 'create') idempotency.onRequestFailed(err);
      if (mode === 'edit' && isRequestOutcomeUnknown(err)) {
        await resolveUncertainUpdate(sent);
        return;
      }
      const invalid = templateInvalidProblems(err);
      const code = apiErrorCode(err);
      if (invalid) {
        setSaveProblems(invalid);
        setFormError(t('La plantilla tiene errores. Revisalos junto a cada campo.'));
      } else if (code === 'template_version_conflict') {
        setConflict(true);
      } else if (code === 'template_name_taken') {
        setSaveProblems({
          errors: [{ code, field: 'name', message: t('Ya existe una plantilla con ese nombre para este canal.') }],
          warnings: [],
        });
      } else {
        setFormError(templateDraftErrorText(err, t, 'No se pudo guardar la plantilla.'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Tras el 409 de versión: se trae la vigente y reemplaza lo escrito (el operador ya fue
  // avisado de copiar lo que quiera conservar).
  const loadCurrentVersion = async () => {
    setReloading(true);
    const fresh = await onReloadTemplate(base.id);
    setReloading(false);
    if (fresh === undefined) {
      setFormError(t('No se pudo cargar la versión actual. Probá de nuevo.'));
      return;
    }
    setConflict(false);
    if (fresh === null) {
      setFormError(t('La plantilla ya no existe o fue archivada.'));
      return;
    }
    const next = draftFromTemplate(fresh);
    setBase({ id: fresh.id, version: fresh.version, draft: next });
    setDraft(next);
    setSaveProblems(null);
    setFormError(null);
  };

  const insertVariable = (token) => (target === 'subject' && isEmail ? insertInSubject(token) : insertInBody(token));
  const channelLabel = t(CHANNEL_LABELS[channel]);
  const title =
    mode === 'create'
      ? t('Nueva plantilla de {{canal}}', { canal: channelLabel })
      : t('Editar plantilla de {{canal}}', { canal: channelLabel });
  const description =
    mode === 'edit'
      ? `${t('Versión {{n}}', { n: base.version })} · ${t('Guardar crea una versión nueva: los envíos ya programados conservan la que tenían.')}`
      : t('Escribí el mensaje con las variables: cada comercio lo recibe con sus datos.');
  const ids = {
    name: `${baseId}-name`,
    subject: `${baseId}-subject`,
    body: `${baseId}-body`,
  };
  const bodyLength = templateBodyLength(channel, draft.body);

  return (
    <Drawer
      open
      size="full"
      title={title}
      description={description}
      onClose={requestClose}
      headerActions={
        <Button onClick={handleSave} loading={saving} disabled={conflict || reloading} iconLeft={<Save className="h-4 w-4" />}>
          {t('Guardar')}
        </Button>
      }
    >
      <div className="space-y-4">
        {confirmDiscard && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning" role="alert">
            <span>{t('Tenés cambios sin guardar.')}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirmDiscard(false)}>
                {t('Seguir editando')}
              </Button>
              <Button size="sm" variant="danger" onClick={onClose}>
                {t('Descartar cambios')}
              </Button>
            </div>
          </div>
        )}

        {conflict && (
          <div className="space-y-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning" role="alert">
            <p className="m-0">
              {t('Otra persona modificó esta plantilla mientras la editabas. Cargá la versión actual antes de guardar.')}
            </p>
            <p className="m-0 text-xs">{t('Se reemplaza lo que escribiste: copiá antes lo que quieras conservar.')}</p>
            <Button size="sm" variant="outline" loading={reloading} onClick={loadCurrentVersion}>
              {t('Cargar la versión actual')}
            </Button>
          </div>
        )}

        {formError && (
          <p className="m-0 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {formError}
          </p>
        )}

        {/* En móvil no entran las dos columnas: se alterna entre editar y ver. */}
        <div className="flex gap-2 lg:hidden" role="group" aria-label={t('Vista del editor')}>
          <Button size="sm" variant={pane === 'edit' ? 'primary' : 'outline'} aria-pressed={pane === 'edit'} onClick={() => setPane('edit')}>
            {t('Editar')}
          </Button>
          <Button
            size="sm"
            variant={pane === 'preview' ? 'primary' : 'outline'}
            aria-pressed={pane === 'preview'}
            onClick={() => setPane('preview')}
          >
            {t('Vista previa')}
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className={cn('min-w-0 space-y-4', pane !== 'edit' && 'hidden lg:block')}>
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <FieldLabel htmlFor={ids.name}>{t('Nombre de la plantilla')}</FieldLabel>
                <Counter length={draft.name.length} max={limits?.nameMaxLength} />
              </div>
              <Input
                id={ids.name}
                value={draft.name}
                invalid={nameProblems.errors.length > 0}
                aria-describedby={`${ids.name}-hint`}
                placeholder={t('Ej.: Recordatorio amable')}
                onChange={(e) => setField('name')(e.target.value)}
              />
              <ProblemList id={`${ids.name}-problems`} {...nameProblems} />
              <p id={`${ids.name}-hint`} className="m-0 text-xs text-fg-muted">
                {t('Solo lo ves vos, para elegirla. El comercio no lo recibe.')}
              </p>
            </div>

            {isEmail && (
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <FieldLabel htmlFor={ids.subject}>{t('Asunto')}</FieldLabel>
                  <Counter length={draft.subject.length} max={limits?.subjectMaxLength} />
                </div>
                <Input
                  id={ids.subject}
                  ref={subjectRef}
                  value={draft.subject}
                  invalid={subjectProblems.errors.length > 0}
                  aria-describedby={`${ids.subject}-problems`}
                  onFocus={() => setTarget('subject')}
                  onChange={(e) => setField('subject')(e.target.value)}
                />
                <ProblemList id={`${ids.subject}-problems`} {...subjectProblems} />
              </div>
            )}

            {variables.length > 0 && (
              <div className="space-y-1.5">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
                  {t('Variables')}
                </span>
                <p className="m-0 text-xs text-fg-muted">
                  {isEmail
                    ? t('Tocá una para insertarla donde está el cursor, en el asunto o en el texto.')
                    : t('Tocá una para insertarla donde está el cursor.')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {variables.map((variable) => {
                    const blocked = isEmail && target === 'subject' && !variable.allowedInSubject;
                    return (
                      <Button
                        key={variable.name}
                        size="sm"
                        variant="outline"
                        disabled={blocked}
                        // Rótulo y descripción vienen de billing (en español): van como texto.
                        title={blocked ? t('No se puede usar en el asunto.') : variable.description}
                        onClick={() => insertVariable(variable.token)}
                      >
                        <code className="text-xs">{variable.token}</code>
                        <span className="text-xs text-fg-muted">{variable.label}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <FieldLabel htmlFor={ids.body}>{t('Texto')}</FieldLabel>
                <Counter length={bodyLength} max={bodyMaxLength(limits, channel)} />
              </div>
              <Textarea
                id={ids.body}
                ref={bodyRef}
                rows={12}
                value={draft.body}
                invalid={bodyProblems.errors.length > 0}
                aria-describedby={`${ids.body}-problems ${ids.body}-hint`}
                onFocus={() => setTarget('body')}
                onChange={(e) => setField('body')(e.target.value)}
              />
              <ProblemList id={`${ids.body}-problems`} {...bodyProblems} />
              <p id={`${ids.body}-hint`} className="m-0 text-xs text-fg-muted">
                {isEmail ? (
                  t('El texto va dentro del diseño del correo, con el logo, la tabla de notas y el botón para pagar. No admite formato ni enlaces propios.')
                ) : (
                  <>
                    {t('Admite el formato de WhatsApp: *negrita*, _cursiva_, ~tachado~ y ```código```.')}{' '}
                    {t('El contador no incluye la lista de notas, que ocupa el lugar que quede libre del mensaje:')}{' '}
                    <code>{'{{detalle_notas}}'}</code>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className={cn('min-w-0', pane !== 'preview' && 'hidden lg:block')}>
            <TemplatePreviewPanel
              channel={channel}
              preview={preview}
              source={source}
              onSourceChange={setSource}
              companyId={companyId}
              onCompanyChange={setCompanyId}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}
